import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
}

export type AIAccessLevel = 'super_admin' | 'tenant_admin' | 'user' | 'none';

export interface TenantClaims {
  tenantId: string | null;
  tenantSlug: string | null;
}

export interface MFAChallengeData {
  mfa_required: true;
  mfa_token: string;
  mfa_methods: string[];
}

export type LoginResult = boolean | MFAChallengeData;

export interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  accessToken: string | null;
  // Tenant info from JWT claims
  tenantId: string | null;
  tenantSlug: string | null;
  aiAccessLevel: AIAccessLevel;
  authMethod: string | null;  // WQ-077: "sso" | "password" | null
  // WQ-342 F2: why the last session ended (shown on /login), cleared on next login
  sessionEndedMessage: string | null;
  clearSessionEndedMessage: () => void;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<LoginResult>;
  loginWithTokens: (data: { access_token: string; refresh_token: string; user: Record<string, unknown> }, rememberMe?: boolean) => void;
  logout: () => void;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
  refreshToken: () => Promise<string | null>;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Parse JWT token to extract claims (without validation - that's done server-side)
 */
export function parseJwtClaims(token: string): { tid?: string; org?: string; role?: string; amr?: string } {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      tid: payload.tid || payload.tenant_id,
      org: payload.org || payload.tenant_slug,
      role: payload.role,
      amr: payload.amr,  // WQ-077: Authentication method reference
    };
  } catch {
    return {};
  }
}

/**
 * Determine AI access level from user role
 */
export function determineAIAccessLevel(role: string | undefined): AIAccessLevel {
  if (!role) return 'none';

  const normalizedRole = role.toLowerCase();

  if (normalizedRole === 'super_admin' || normalizedRole === 'superadmin') {
    return 'super_admin';
  }
  if (normalizedRole === 'admin' || normalizedRole === 'tenant_admin') {
    return 'tenant_admin';
  }
  if (normalizedRole === 'user' || normalizedRole === 'member') {
    return 'user';
  }

  return 'none';
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Tenant info from JWT claims
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);
  const [aiAccessLevel, setAIAccessLevel] = useState<AIAccessLevel>('none');

  // WQ-077: Auth method tracking for Single Logout (SLO)
  const [authMethod, setAuthMethod] = useState<string | null>(null);
  const [sessionEndedMessage, setSessionEndedMessage] = useState<string | null>(null);

  // Use refs to avoid stale closure issues
  const accessTokenRef = useRef<string | null>(null);
  const refreshTokenRef = useRef<string | null>(null);
  const refreshPromiseRef = useRef<Promise<string | null> | null>(null);
  const isLoggingOutRef = useRef(false);

  const getStorage = useCallback(() => {
    return localStorage.getItem('remember_me') === 'true' ? localStorage : sessionStorage;
  }, []);

  // Helper to get the current token - always reads from ref first, then storage
  const getCurrentToken = useCallback(() => {
    // Ref is always up-to-date (synchronous)
    if (accessTokenRef.current) {
      return accessTokenRef.current;
    }
    // Fallback to storage
    const storage = getStorage();
    return storage.getItem('access_token');
  }, [getStorage]);

  const loginWithTokens = useCallback((data: { access_token: string; refresh_token: string; user: Record<string, unknown> }, rememberMe: boolean = false) => {
    const storage = rememberMe ? localStorage : sessionStorage;

    // Store remember preference
    localStorage.setItem('remember_me', rememberMe.toString());

    // Clear the other storage to prevent conflicts
    const otherStorage = rememberMe ? sessionStorage : localStorage;
    ['access_token', 'refresh_token', 'user'].forEach(k => otherStorage.removeItem(k));

    // Update refs and state
    accessTokenRef.current = data.access_token;
    refreshTokenRef.current = data.refresh_token;
    setAccessToken(data.access_token);
    setSessionEndedMessage(null);

    const userRole = (data.user?.role as string) || 'user';
    setUser({
      id: (data.user?.id as string) || '',
      email: (data.user?.email as string) || '',
      name: (data.user?.name as string) || '',
      role: userRole,
    });

    const claims = parseJwtClaims(data.access_token);
    setTenantId(claims.tid || null);
    setTenantSlug(claims.org || null);
    setAIAccessLevel(determineAIAccessLevel(userRole));

    // WQ-077: Track auth method from JWT amr claim
    setAuthMethod(claims.amr || null);

    setIsAuthenticated(true);
    isLoggingOutRef.current = false;

    // Persist to storage
    storage.setItem('access_token', data.access_token);
    storage.setItem('refresh_token', data.refresh_token);
    storage.setItem('user', JSON.stringify(data.user));

    // WQ-077: Store auth method and SSO tenant slug for SLO
    if (claims.amr) {
      storage.setItem('auth_method', claims.amr);
    }
    if (claims.amr === 'sso' && claims.org) {
      storage.setItem('sso_tenant_slug', claims.org);
    }

    console.log('[Auth] Login with tokens successful');
  }, []);

  const login = useCallback(async (email: string, password: string, rememberMe: boolean = false): Promise<LoginResult> => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const data = await response.json();

        // Check for MFA challenge response (WQ-067)
        if (data.mfa_required) {
          console.log('[Auth] MFA challenge required');
          // Save remember preference for after MFA completes
          localStorage.setItem('remember_me', rememberMe.toString());
          if (rememberMe) {
            localStorage.setItem('remembered_email', email);
          }
          return {
            mfa_required: true,
            mfa_token: data.mfa_token,
            mfa_methods: data.mfa_methods || ['totp', 'recovery'],
          } as MFAChallengeData;
        }

        // Normal login — store tokens
        loginWithTokens(data, rememberMe);

        // WQ-077: Explicit password auth method (JWT may not have amr for password)
        const authStorage = rememberMe ? localStorage : sessionStorage;
        authStorage.setItem('auth_method', 'password');

        // Save or clear remembered email
        if (rememberMe) {
          localStorage.setItem('remembered_email', email);
        } else {
          localStorage.removeItem('remembered_email');
        }

        console.log('[Auth] Login successful');
        return true;
      }
      return false;
    } catch (error) {
      console.error('[Auth] Login error:', error);
      return false;
    }
  }, [loginWithTokens]);

  // Clear tokens, user and tenant state from memory and both storages.
  // Note: we keep the remember_me preference.
  const clearLocalSession = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    setAccessToken(null);
    setTenantId(null);
    setTenantSlug(null);
    setAIAccessLevel('none');
    setAuthMethod(null);
    accessTokenRef.current = null;
    refreshTokenRef.current = null;
    refreshPromiseRef.current = null;

    // Clear both storages (including WQ-077 keys)
    ['access_token', 'refresh_token', 'user', 'auth_method', 'sso_tenant_slug'].forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  }, []);

  const logout = useCallback(async () => {
    // Prevent multiple logouts
    if (isLoggingOutRef.current) {
      console.log('[Auth] Already logging out, skipping');
      return;
    }
    isLoggingOutRef.current = true;

    console.log('[Auth] Logging out');

    // WQ-077: Capture SSO state from storage before clearing (avoids stale closure)
    const currentStorage = localStorage.getItem('remember_me') === 'true' ? localStorage : sessionStorage;
    const wasSSO = currentStorage.getItem('auth_method') === 'sso';
    const ssoTenantSlug = currentStorage.getItem('sso_tenant_slug');

    clearLocalSession();

    // WQ-077: Single Logout — redirect to IdP logout for SSO sessions
    if (wasSSO) {
      try {
        const params = ssoTenantSlug ? `?tenant=${encodeURIComponent(ssoTenantSlug)}` : '';
        const response = await fetch(`/api/auth/sso/logout-url${params}`);
        if (response.ok) {
          const data = await response.json();
          if (data.logout_url) {
            console.log('[Auth] SLO: Redirecting to IdP logout');
            window.location.href = data.logout_url;
            return;
          }
        }
      } catch (e) {
        console.error('[Auth] SLO redirect failed:', e);
      }
    }
  }, [clearLocalSession]);

  // WQ-342 F2: the server rejected the session at refresh (401/403), e.g. a
  // pre-v5.331.0 SSO token with no tenant. End it locally — no backend logout
  // and no SSO single-logout redirect. Clearing isAuthenticated lets
  // ProtectedRoute send the user to /login with the return path in state.from;
  // the backend's message is shown there.
  const endSession = useCallback((message: string) => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    console.warn('[Auth] Session ended by server:', message);
    clearLocalSession();
    setSessionEndedMessage(message);
  }, [clearLocalSession]);

  const clearSessionEndedMessage = useCallback(() => setSessionEndedMessage(null), []);

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    // If already refreshing, return the existing promise
    if (refreshPromiseRef.current) {
      console.log('[Auth] Refresh already in progress, waiting...');
      return refreshPromiseRef.current;
    }

    const storage = getStorage();
    const refreshToken = refreshTokenRef.current || storage.getItem('refresh_token');

    if (!refreshToken) {
      console.log('[Auth] No refresh token available');
      return null;
    }

    console.log('[Auth] Starting token refresh');

    // Create and store the refresh promise
    refreshPromiseRef.current = (async () => {
      try {
        const response = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken })
        });

        if (response.ok) {
          const data = await response.json();
          console.log('[Auth] Token refresh successful');

          // Update refs first (synchronous)
          accessTokenRef.current = data.access_token;
          if (data.refresh_token) {
            refreshTokenRef.current = data.refresh_token;
            storage.setItem('refresh_token', data.refresh_token);
          }

          // Update state and storage
          setAccessToken(data.access_token);
          storage.setItem('access_token', data.access_token);

          return data.access_token;
        }

        console.warn('[Auth] Token refresh failed with status:', response.status);

        // WQ-342 F2: 401/403 means the server rejected this session — end it and
        // go to sign-in. 5xx and network errors keep the old behaviour (transient).
        if (response.status === 401 || response.status === 403) {
          let detail = '';
          try {
            // The API's error envelope is {error: {code, message}}; bare FastAPI is {detail}
            const body = await response.json();
            if (typeof body?.error?.message === 'string') detail = body.error.message;
            else if (typeof body?.detail === 'string') detail = body.detail;
          } catch {
            // non-JSON body — use the generic message
          }
          endSession(detail || 'Your session has ended. Please sign in again.');
        }
        return null;
      } catch (error) {
        console.error('[Auth] Token refresh error:', error);
        return null;
      } finally {
        // Clear the promise after a short delay to handle rapid calls
        setTimeout(() => { refreshPromiseRef.current = null; }, 500);
      }
    })();

    return refreshPromiseRef.current;
  }, [getStorage, endSession]);

  const isTokenExpiringSoon = useCallback((token: string, bufferMs: number = 5 * 60 * 1000): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) return false;
      const expiresAt = payload.exp * 1000;
      return (expiresAt - Date.now()) < bufferMs;
    } catch {
      return false;
    }
  }, []);

  const authFetch = useCallback(async (url: string, options: RequestInit = {}): Promise<Response> => {
    // Don't make requests if we're logging out
    if (isLoggingOutRef.current) {
      console.log('[authFetch] Skipping request during logout:', url);
      return new Response(JSON.stringify({ error: 'Logging out' }), {
        status: 401,
        headers: { 'Content-Type': 'application/json' }
      });
    }

    // Always get fresh token (avoid stale closure)
    let token = getCurrentToken();

    console.log(`[authFetch] ${options.method || 'GET'} ${url} - hasToken: ${!!token}`);

    const makeRequest = async (authToken: string | null) => {
      const headers = new Headers(options.headers || {});
      if (authToken) {
        headers.set('Authorization', `Bearer ${authToken}`);
      }
      if (!headers.has('Content-Type') && options.body) {
        headers.set('Content-Type', 'application/json');
      }
      return fetch(url, { ...options, headers });
    };

    // If no token, try to get from storage one more time
    if (!token) {
      const storage = getStorage();
      token = storage.getItem('access_token');
      if (token) {
        accessTokenRef.current = token;
      }
    }

    // Still no token - return 401 but don't logout (might still be loading)
    if (!token) {
      console.warn('[authFetch] No token available for:', url);
      return new Response(JSON.stringify({ error: 'Not authenticated' }), {
        status: 401,
        headers: { 'Content-Type': 'application/json' }
      });
    }

    // Proactive refresh: if token expires within 5 minutes, refresh before the request
    if (isTokenExpiringSoon(token)) {
      console.log('[authFetch] Token expiring soon, proactively refreshing...');
      const newToken = await refreshAccessToken();
      if (newToken) {
        token = newToken;
      } else if (isLoggingOutRef.current) {
        // WQ-342 F2: the refresh ended the session — don't send the stale token
        return new Response(JSON.stringify({ error: 'Session ended' }), {
          status: 401,
          headers: { 'Content-Type': 'application/json' }
        });
      }
      // If refresh fails, proceed with current token — the 401 fallback still works
    }

    try {
      let response = await makeRequest(token);
      console.log(`[authFetch] ${url} - status: ${response.status}`);

      // If 401, try to refresh token and retry
      if (response.status === 401) {
        console.log('[authFetch] Got 401, attempting token refresh...');

        const newToken = await refreshAccessToken();

        if (newToken) {
          response = await makeRequest(newToken);
          console.log(`[authFetch] Retry after refresh - status: ${response.status}`);

          // If still 401 after refresh, the session is truly invalid
          if (response.status === 401) {
            console.warn('[authFetch] Still 401 after refresh - session invalid');
            // Only logout if refresh succeeded but request still failed (token truly invalid)
            logout();
          }
        } else {
          // Refresh failed - check if we still have a token in storage
          // If so, don't logout yet - might be a transient network issue
          const storedToken = getStorage().getItem('access_token');
          if (!storedToken) {
            console.error('[authFetch] No stored token and refresh failed - logging out');
            logout();
          } else {
            console.warn('[authFetch] Refresh failed but token exists - not logging out');
            // Return the 401 response but don't logout - let the user retry
          }
        }
      }

      return response;
    } catch (error) {
      // Handle AbortError gracefully - request was cancelled (e.g., user navigated away)
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('[authFetch] Request cancelled:', url);
        // Return a cancelled response - don't treat as auth failure
        return new Response(JSON.stringify({ error: 'Request cancelled' }), {
          status: 0,
          headers: { 'Content-Type': 'application/json' }
        });
      }
      // Network errors - don't logout, just re-throw
      console.error('[authFetch] Network error:', error);
      throw error;
    }
  }, [getCurrentToken, getStorage, refreshAccessToken, isTokenExpiringSoon, logout]);

  // Restore session on mount
  React.useEffect(() => {
    const restoreSession = async () => {
      // === SSO Code Exchange (WQ-073) ===
      const urlParams = new URLSearchParams(window.location.search);
      const ssoCode = urlParams.get('sso_code');
      const ssoError = urlParams.get('sso_error');

      // Clean URL params regardless of outcome
      if (ssoCode || ssoError) {
        window.history.replaceState({}, '', window.location.pathname);
      }

      if (ssoError) {
        console.error('[Auth] SSO error:', ssoError);
        setIsLoading(false);
        return;
      }

      if (ssoCode) {
        try {
          console.log('[Auth] Exchanging SSO code for tokens...');
          const response = await fetch(`/api/auth/sso/exchange?code=${encodeURIComponent(ssoCode)}`);
          if (response.ok) {
            const data = await response.json();
            loginWithTokens(data, true); // SSO users get persistent sessions
            console.log('[Auth] SSO login successful');
            setIsLoading(false);
            return;
          } else {
            console.error('[Auth] SSO code exchange failed:', response.status);
          }
        } catch (error) {
          console.error('[Auth] SSO code exchange error:', error);
        }
        setIsLoading(false);
        return;
      }

      // === Existing session restore logic (unchanged) ===
      const rememberMe = localStorage.getItem('remember_me') === 'true';
      const storage = rememberMe ? localStorage : sessionStorage;
      const storedToken = storage.getItem('access_token');
      const storedRefreshToken = storage.getItem('refresh_token');
      const storedUser = storage.getItem('user');

      console.log('[Auth] Restoring session, rememberMe:', rememberMe, 'hasToken:', !!storedToken);

      if (storedToken && storedRefreshToken && storedUser) {
        try {
          // Set refs FIRST (synchronous, no stale closure issues)
          accessTokenRef.current = storedToken;
          refreshTokenRef.current = storedRefreshToken;

          // Parse user and extract role
          const parsedUser = JSON.parse(storedUser);

          // Then set state
          setAccessToken(storedToken);
          setUser(parsedUser);
          setIsAuthenticated(true);
          isLoggingOutRef.current = false;

          // Restore tenant info from JWT token
          const claims = parseJwtClaims(storedToken);
          setTenantId(claims.tid || null);
          setTenantSlug(claims.org || null);
          setAIAccessLevel(determineAIAccessLevel(parsedUser?.role));

          // WQ-077: Restore auth method
          setAuthMethod(storage.getItem('auth_method') || null);

          console.log('[Auth] Session restored successfully');
        } catch (e) {
          console.error('[Auth] Failed to parse stored user data:', e);
          ['access_token', 'refresh_token', 'user'].forEach(k => storage.removeItem(k));
        }
      } else {
        console.log('[Auth] No stored session found');
      }

      setIsLoading(false);
    };

    restoreSession();
  }, []);

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      user,
      accessToken,
      tenantId,
      tenantSlug,
      aiAccessLevel,
      authMethod,
      sessionEndedMessage,
      clearSessionEndedMessage,
      login,
      loginWithTokens,
      logout,
      authFetch,
      refreshToken: refreshAccessToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// =============================================================================
// Convenience Hook: useAuthWithTenant
// =============================================================================

// Optional TenantContext integration - only used if TenantProvider is in the tree
// This avoids circular dependency since TenantContext imports AuthContext
let useTenantOptionalFn: (() => {
  tenant: unknown;
  error: string | null;
  isLoading: boolean;
  isAIEnabled: () => boolean;
  getAllowedModels: () => string[];
  canUseAITool: (tool: string) => boolean;
  hasFeature: (feature: string) => boolean;
  refreshTenant: () => Promise<void>;
} | null) | null = null;

/**
 * Register the TenantContext's useTenantOptional hook.
 * Called by TenantContext to enable the useAuthWithTenant convenience hook.
 * @internal
 */
export function registerTenantOptionalHook(hook: typeof useTenantOptionalFn) {
  useTenantOptionalFn = hook;
}

/**
 * Convenience hook that combines AuthContext with TenantContext.
 * Provides unified access to user, tenant, and AI access information.
 *
 * Note: This hook requires both AuthProvider and TenantProvider to be in the tree.
 * Use this when you need both auth and full tenant details in a component.
 */
export function useAuthWithTenant() {
  const auth = useAuth();

  // Use registered tenant hook if available
  const tenantContext = useTenantOptionalFn?.() ?? null;

  return {
    // Auth info
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading || (tenantContext?.isLoading ?? false),
    user: auth.user,
    accessToken: auth.accessToken,

    // Basic tenant info from JWT (always available when authenticated)
    tenantId: auth.tenantId,
    tenantSlug: auth.tenantSlug,
    aiAccessLevel: auth.aiAccessLevel,

    // Full tenant details (from TenantContext, may be null for non-admin users)
    tenant: tenantContext?.tenant ?? null,
    tenantError: tenantContext?.error ?? null,

    // AI capabilities
    isAIEnabled: tenantContext?.isAIEnabled?.() ?? (auth.aiAccessLevel === 'super_admin'),
    getAllowedModels: tenantContext?.getAllowedModels ?? (() => []),
    canUseAITool: tenantContext?.canUseAITool ?? (() => false),
    hasFeature: tenantContext?.hasFeature ?? (() => false),

    // Auth methods
    login: auth.login,
    logout: auth.logout,
    authFetch: auth.authFetch,
    refreshToken: auth.refreshToken,

    // Tenant refresh
    refreshTenant: tenantContext?.refreshTenant ?? (async () => {}),
  };
}

export default AuthContext;
